import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PaymentsIcon from '@mui/icons-material/Payments';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { codeFont, numFont } from '../../utils/fonts';
import { formatDate, formatDateOnly, formatQuantity } from '../../utils';
import { formatPrice, formatPriceWithCurrency } from '../../utils/vat';
import CardHeader from '../shared/CardHeader';
import DecimalField from '../shared/DecimalField';
import ReasonDialog from './ReasonDialog';
import VendorAutocomplete from './VendorAutocomplete';
import { FlagIcons, KindChip, StatusChip } from './PurchaseChips';
import {
  FLAG_SEVERITY,
  KINDS,
  PAYMENT_METHODS,
  PL_API,
  RECEIPT_METHODS,
  REFERENCE_TYPES,
  highlightedFields,
  invoiceUrl,
  newIdempotencyKey,
  purchaseErrorMessage,
  toNumber,
} from './purchaseUtils';

const EDITABLE_FIELDS = [
  'kind',
  'vendor_name_on_document',
  'invoice_number',
  'invoice_date',
  'due_date',
  'total_net',
  'total_vat',
  'total_gross',
  'reference_number',
  'reference_type',
  'payee_iban',
  'payee_bic',
  'order_reference',
  'delivery_note_reference',
  'cost_category',
  'description',
  'notes',
];

const warnSx = (on) =>
  on
    ? {
        '& .MuiOutlinedInput-root fieldset': { borderColor: 'warning.main', borderWidth: 2 },
        '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'warning.dark' },
      }
    : {};

function money(value) {
  return formatPriceWithCurrency(toNumber(value));
}

// ---------------------------------------------------------------------------------------
// Document pane
// ---------------------------------------------------------------------------------------

function DocumentPane({ invoice, canManage, onAttachmentAdded }) {
  const { t } = useTranslation();
  const attachments = invoice.attachments || [];
  const viewable = attachments.filter((a) => a.is_viewable_inline);
  const [selectedId, setSelectedId] = useState(viewable[0]?.id || attachments[0]?.id || null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!attachments.some((a) => a.id === selectedId)) {
      setSelectedId(viewable[0]?.id || attachments[0]?.id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id, attachments.length]);

  const current = attachments.find((a) => a.id === selectedId);

  const addAttachment = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(invoiceUrl(invoice.id, 'attachments'), form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onAttachmentAdded?.();
    } catch (error) {
      // the parent shows errors through its snackbar on reload failure
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: { xs: 360, md: '70vh' } }} data-testid="pl-document-pane">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <AttachFileIcon fontSize="small" color="action" />
        {attachments.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('purchaseLedger.attachments.none')}
          </Typography>
        )}
        {attachments.map((a) => (
          <Chip
            key={a.id}
            size="small"
            label={a.original_filename || t(`purchaseLedger.attachments.kind.${a.kind}`, { defaultValue: a.kind })}
            color={a.id === selectedId ? 'primary' : 'default'}
            variant={a.id === selectedId ? 'filled' : 'outlined'}
            onClick={() => setSelectedId(a.id)}
            data-testid={`pl-attachment-${a.id}`}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        {current && (
          <Button size="small" startIcon={<DownloadIcon />} component="a" href={current.download_url} target="_blank" rel="noopener" data-testid="pl-attachment-download">
            {t('purchaseLedger.attachments.download')}
          </Button>
        )}
        {canManage && (
          <Button size="small" variant="outlined" component="label" disabled={uploading}>
            {t('purchaseLedger.attachments.add')}
            <input
              type="file"
              hidden
              accept="application/pdf,image/*,.xml"
              onChange={(e) => {
                if (e.target.files?.[0]) addAttachment(e.target.files[0]);
                e.target.value = '';
              }}
            />
          </Button>
        )}
      </Box>
      <Box sx={{ flex: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'stretch', justifyContent: 'center', minHeight: 320 }}>
        {!current && (
          <Typography color="text.secondary" sx={{ alignSelf: 'center', p: 3, textAlign: 'center' }}>
            {t('purchaseLedger.attachments.noPreview')}
          </Typography>
        )}
        {current && current.content_type === 'application/pdf' && (
          <iframe title={current.original_filename || 'document'} src={current.download_url} style={{ border: 0, width: '100%', minHeight: 480, flex: 1 }} data-testid="pl-document-iframe" />
        )}
        {current && current.content_type?.startsWith('image/') && (
          <Box sx={{ p: 1, overflow: 'auto', width: '100%', textAlign: 'center' }}>
            <img src={current.download_url} alt={current.original_filename || 'document'} style={{ maxWidth: '100%' }} data-testid="pl-document-image" />
          </Box>
        )}
        {current && !current.is_viewable_inline && (
          <Box sx={{ alignSelf: 'center', textAlign: 'center', p: 3 }}>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {t('purchaseLedger.attachments.noPreview')}
            </Typography>
            <Button variant="outlined" startIcon={<DownloadIcon />} component="a" href={current.download_url} target="_blank" rel="noopener">
              {current.original_filename}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Header field editor
// ---------------------------------------------------------------------------------------

function FieldsCard({ invoice, categories, editable, onSaved, notify }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const highlighted = useMemo(() => highlightedFields(invoice), [invoice]);

  const startEdit = () => {
    const next = {};
    EDITABLE_FIELDS.forEach((f) => {
      next[f] = invoice[f] ?? '';
    });
    next.cost_category = invoice.cost_category || '';
    setForm(next);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    EDITABLE_FIELDS.forEach((f) => {
      const value = form[f];
      const original = invoice[f] ?? '';
      if (String(value ?? '') !== String(original ?? '')) {
        payload[f] = value === '' && ['invoice_date', 'due_date', 'cost_category', 'total_net', 'total_vat'].includes(f) ? null : value;
      }
    });
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      setSaving(false);
      return;
    }
    try {
      const response = await axios.patch(invoiceUrl(invoice.id), payload);
      onSaved(response.data);
      setEditing(false);
      notify(t('purchaseLedger.detail.saved'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setText = (field) => (e) => set(field)(e.target.value);
  const fieldSx = (name) => ({ ...warnSx(highlighted.has(name)) });

  const row = (label, value, name) => (
    <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ ...numFont, textAlign: 'right', fontWeight: 500, color: highlighted.has(name) ? 'warning.dark' : 'inherit' }} data-testid={`pl-field-${name}`}>
        {value || '-'}
      </Typography>
    </Box>
  );

  const categoryName = (invoice.cost_category_name || (categories.find((c) => c.id === invoice.cost_category)?.name) || '');

  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }}>
      <CardHeader
        title={t('purchaseLedger.detail.fields')}
        action={
          editable && !editing ? (
            <Button size="small" startIcon={<EditIcon />} onClick={startEdit} data-testid="pl-edit-fields">
              {t('Edit')}
            </Button>
          ) : null
        }
      />
      <Box sx={{ p: 2 }}>
        {!editing ? (
          <>
            {row(t('purchaseLedger.fields.kind'), t(`purchaseLedger.kind.${invoice.kind}`), 'kind')}
            {row(t('purchaseLedger.fields.vendorNameOnDocument'), invoice.vendor_name_on_document, 'vendor_name_on_document')}
            {row(t('purchaseLedger.fields.invoiceNumber'), invoice.invoice_number, 'invoice_number')}
            {row(t('purchaseLedger.fields.invoiceDate'), formatDateOnly(invoice.invoice_date), 'invoice_date')}
            {row(t('purchaseLedger.fields.dueDate'), formatDateOnly(invoice.due_date), 'due_date')}
            {row(t('purchaseLedger.fields.totalNet'), money(invoice.total_net), 'total_net')}
            {row(t('purchaseLedger.fields.totalVat'), money(invoice.total_vat), 'total_vat')}
            {row(t('purchaseLedger.fields.totalGross'), money(invoice.total_gross), 'total_gross')}
            {invoice.currency && invoice.currency !== 'EUR' && row(t('purchaseLedger.fields.currency'), invoice.currency, 'currency')}
            {row(t('purchaseLedger.fields.reference'), invoice.reference_number ? `${invoice.reference_number} (${t(`purchaseLedger.referenceType.${invoice.reference_type}`)})` : '', 'reference_number')}
            {row('IBAN', invoice.payee_iban, 'payee_iban')}
            {invoice.payee_bic && row('BIC', invoice.payee_bic, 'payee_bic')}
            {row(t('purchaseLedger.fields.orderReference'), invoice.order_reference, 'order_reference')}
            {row(t('purchaseLedger.fields.deliveryNoteReference'), invoice.delivery_note_reference, 'delivery_note_reference')}
            {row(t('purchaseLedger.fields.costCategory'), categoryName, 'cost_category')}
            {row(t('purchaseLedger.fields.description'), invoice.description, 'description')}
            {invoice.notes && row(t('purchaseLedger.fields.notes'), invoice.notes, 'notes')}
            {(invoice.vat_breakdown || []).length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{t('purchaseLedger.fields.vatBreakdown')}</Typography>
                {invoice.vat_breakdown.map((row_, i) => (
                  <Typography key={i} variant="caption" display="block" sx={numFont}>
                    {row_.rate} %: {t('purchaseLedger.fields.totalNet')} {row_.net} · {t('purchaseLedger.fields.totalVat')} {row_.vat}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('purchaseLedger.fields.kind')}</InputLabel>
              <Select value={form.kind} label={t('purchaseLedger.fields.kind')} onChange={setText('kind')}>
                {KINDS.map((k) => (
                  <MenuItem key={k} value={k}>{t(`purchaseLedger.kind.${k}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label={t('purchaseLedger.fields.vendorNameOnDocument')} value={form.vendor_name_on_document} onChange={setText('vendor_name_on_document')} sx={fieldSx('vendor_name_on_document')} />
            <TextField size="small" label={t('purchaseLedger.fields.invoiceNumber')} value={form.invoice_number} onChange={setText('invoice_number')} sx={fieldSx('invoice_number')} inputProps={{ 'data-testid': 'pl-edit-invoice-number' }} />
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField size="small" type="date" fullWidth label={t('purchaseLedger.fields.invoiceDate')} value={form.invoice_date || ''} onChange={setText('invoice_date')} InputLabelProps={{ shrink: true }} sx={fieldSx('invoice_date')} />
              <TextField size="small" type="date" fullWidth label={t('purchaseLedger.fields.dueDate')} value={form.due_date || ''} onChange={setText('due_date')} InputLabelProps={{ shrink: true }} sx={fieldSx('due_date')} inputProps={{ 'data-testid': 'pl-edit-due-date' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <DecimalField size="small" fullWidth label={t('purchaseLedger.fields.totalNet')} value={form.total_net} onChange={set('total_net')} allowNegative sx={fieldSx('total_net')} />
              <DecimalField size="small" fullWidth label={t('purchaseLedger.fields.totalVat')} value={form.total_vat} onChange={set('total_vat')} allowNegative sx={fieldSx('total_vat')} />
              <DecimalField size="small" fullWidth label={t('purchaseLedger.fields.totalGross')} value={form.total_gross} onChange={set('total_gross')} allowNegative sx={fieldSx('total_gross')} inputProps={{ 'data-testid': 'pl-edit-total-gross' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField size="small" fullWidth label={t('purchaseLedger.fields.reference')} value={form.reference_number} onChange={setText('reference_number')} sx={fieldSx('reference_number')} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('purchaseLedger.fields.referenceType')}</InputLabel>
                <Select value={form.reference_type || 'none'} label={t('purchaseLedger.fields.referenceType')} onChange={setText('reference_type')}>
                  {REFERENCE_TYPES.map((r) => (
                    <MenuItem key={r} value={r}>{t(`purchaseLedger.referenceType.${r}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField size="small" fullWidth label="IBAN" value={form.payee_iban} onChange={setText('payee_iban')} sx={fieldSx('payee_iban')} inputProps={{ 'data-testid': 'pl-edit-iban' }} />
              <TextField size="small" label="BIC" value={form.payee_bic} onChange={setText('payee_bic')} sx={{ minWidth: 150 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField size="small" fullWidth label={t('purchaseLedger.fields.orderReference')} value={form.order_reference} onChange={setText('order_reference')} />
              <TextField size="small" fullWidth label={t('purchaseLedger.fields.deliveryNoteReference')} value={form.delivery_note_reference} onChange={setText('delivery_note_reference')} />
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('purchaseLedger.fields.costCategory')}</InputLabel>
              <Select value={form.cost_category || ''} label={t('purchaseLedger.fields.costCategory')} onChange={setText('cost_category')} data-testid="pl-edit-category">
                <MenuItem value=""><em>{t('purchaseLedger.fields.noCategory')}</em></MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label={t('purchaseLedger.fields.description')} value={form.description} onChange={setText('description')} />
            <TextField size="small" label={t('purchaseLedger.fields.notes')} value={form.notes} onChange={setText('notes')} multiline minRows={2} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setEditing(false)} disabled={saving}>{t('Cancel')}</Button>
              <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null} data-testid="pl-save-fields">
                {t('Save')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Vendor card
// ---------------------------------------------------------------------------------------

function VendorCard({ invoice, editable, onSaved, notify }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBid, setCreateBid] = useState('');

  const submit = async (payload) => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, 'set-vendor'), payload);
      onSaved(response.data);
      setPicked(null);
      setCreateOpen(false);
      notify(t('purchaseLedger.vendor.saved'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const vendor = invoice.vendor_detail;
  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }} data-testid="pl-vendor-card">
      <CardHeader title={t('purchaseLedger.fields.vendor')} icon={<StorefrontIcon fontSize="small" />} />
      <Box sx={{ p: 2 }}>
        {vendor ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }} data-testid="pl-vendor-name">{vendor.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {vendor.business_id || t('purchaseLedger.vendor.noBusinessId')}
              </Typography>
              {invoice.payee_iban && (
                <Typography variant="caption" color={invoice.vendor_iban_known === false ? 'error.main' : 'text.secondary'} display="block">
                  {invoice.vendor_iban_known === false ? t('purchaseLedger.vendor.ibanUnknown') : t('purchaseLedger.vendor.ibanKnown')}
                </Typography>
              )}
            </Box>
            <Link component={RouterLink} to={`/ostolaskut?tab=all&vendor=${vendor.id}`} variant="body2">
              {t('purchaseLedger.vendor.history')}
            </Link>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {t('purchaseLedger.vendor.unresolved', { name: invoice.vendor_name_on_document || '-' })}
          </Alert>
        )}
        {editable && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <VendorAutocomplete value={picked} onChange={setPicked} label={vendor ? t('purchaseLedger.vendor.change') : t('purchaseLedger.vendor.pick')} testId="pl-vendor-picker" />
            </Box>
            <Button variant="contained" size="small" disabled={!picked || busy} onClick={() => submit({ vendor: picked.id })} data-testid="pl-vendor-set">
              {t('purchaseLedger.vendor.use')}
            </Button>
            {!vendor && (
              <Button variant="outlined" size="small" disabled={busy} onClick={() => { setCreateName(invoice.vendor_name_on_document || ''); setCreateBid(invoice.vendor_business_id_on_document || ''); setCreateOpen(true); }} data-testid="pl-vendor-create">
                {t('purchaseLedger.vendor.create')}
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('purchaseLedger.vendor.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField autoFocus size="small" label={t('purchaseLedger.fields.vendor')} value={createName} onChange={(e) => setCreateName(e.target.value)} inputProps={{ 'data-testid': 'pl-vendor-create-name' }} />
          <TextField size="small" label={t('purchaseLedger.vendor.businessId')} value={createBid} onChange={(e) => setCreateBid(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={busy}>{t('Cancel')}</Button>
          <Button variant="contained" disabled={busy || !createName.trim()} onClick={() => submit({ create_name: createName.trim(), create_business_id: createBid.trim() })} data-testid="pl-vendor-create-save">
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Links (kohdistus)
// ---------------------------------------------------------------------------------------

function LinksCard({ invoice, editable, onSaved, notify }) {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCandidates = useCallback(async () => {
    try {
      const response = await axios.get(invoiceUrl(invoice.id, 'candidates'));
      setCandidates(response.data || []);
    } catch (error) {
      setCandidates([]);
    }
  }, [invoice.id]);

  useEffect(() => {
    if (editable) loadCandidates();
  }, [editable, loadCandidates, invoice.links?.length]);

  const linked = new Set((invoice.links || []).map((l) => `${l.target_type}:${l.target_id}`));
  const suggestions = (candidates || []).filter((c) => !linked.has(`${c.target_type}:${c.target_id}`));

  const link = async (c) => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, 'links'), { target_type: c.target_type, target_id: c.target_id });
      onSaved(response.data);
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (l) => {
    setBusy(true);
    try {
      const response = await axios.delete(`${invoiceUrl(invoice.id, 'links')}${l.id}/`);
      onSaved(response.data);
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const rematch = async () => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, 'rematch'), {});
      if (response.data?.invoice) onSaved(response.data.invoice);
      setCandidates(response.data?.candidates || []);
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const diff = (value) => {
    if (value === null || value === undefined) return null;
    const n = toNumber(value);
    if (Math.abs(n) < 0.005) return <Chip size="small" color="success" label={t('purchaseLedger.links.matches')} sx={{ height: 20 }} />;
    return <Chip size="small" color="warning" label={`${t('purchaseLedger.links.differenceShort')} ${n > 0 ? '+' : ''}${money(n)}`} sx={{ height: 20, ...numFont }} />;
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }} data-testid="pl-links-card">
      <CardHeader
        title={t('purchaseLedger.links.title')}
        icon={<LinkIcon fontSize="small" />}
        action={
          editable ? (
            <Button size="small" startIcon={<RefreshIcon />} disabled={busy} onClick={rematch} data-testid="pl-rematch">
              {t('purchaseLedger.links.rematch')}
            </Button>
          ) : null
        }
      />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(invoice.links || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">{t('purchaseLedger.links.none')}</Typography>
        )}
        {(invoice.links || []).map((l) => (
          <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid={`pl-link-${l.id}`}>
            <Chip size="small" variant="outlined" label={t(`purchaseLedger.links.targetType.${l.target_type}`)} />
            <Link component={RouterLink} to={l.target?.url || '#'} sx={{ fontWeight: 600 }}>
              {l.target?.label || `#${l.target_id}`}
            </Link>
            {l.target?.expected_gross != null && (
              <Typography variant="caption" color="text.secondary" sx={numFont}>{money(l.target.expected_gross)}</Typography>
            )}
            {diff(l.difference_gross)}
            {l.source === 'auto' && <Chip size="small" label={t('purchaseLedger.links.auto')} sx={{ height: 20 }} />}
            <Box sx={{ flex: 1 }} />
            {editable && (
              <IconButton size="small" disabled={busy} onClick={() => unlink(l)} data-testid={`pl-unlink-${l.id}`}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {editable && suggestions.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.links.suggestions')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {suggestions.slice(0, 6).map((c) => (
                <Tooltip key={`${c.target_type}-${c.target_id}`} title={(c.reasons || []).map((r) => t(`purchaseLedger.links.reasons.${r}`, { defaultValue: r })).join(', ')}>
                  <Chip
                    size="small"
                    clickable
                    color="primary"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => link(c)}
                    label={`${t(`purchaseLedger.links.targetType.${c.target_type}`)} ${c.label || `#${c.target_id}`} · ${Math.round(toNumber(c.score) * 100)} %`}
                    data-testid={`pl-candidate-${c.target_type}-${c.target_id}`}
                  />
                </Tooltip>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Approval bar
// ---------------------------------------------------------------------------------------

function ApprovalBar({ invoice, canApprove, canManage, onSaved, notify }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [totalsChecked, setTotalsChecked] = useState(false);
  const [acceptIban, setAcceptIban] = useState(false);
  const [receiptMethod, setReceiptMethod] = useState('card');
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [unapproveOpen, setUnapproveOpen] = useState(false);

  const missing = invoice.approval_missing || [];
  const blocking = missing.filter((m) => !(m === 'totals_mismatch' && totalsChecked) && !(m === 'new_iban' && acceptIban));

  const post = async (action, payload = {}, done) => {
    setBusy(action);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, action), payload);
      onSaved(response.data);
      if (done) notify(done);
      return true;
    } catch (error) {
      const data = error.response?.data;
      const list = data?.missing?.length ? ` ${data.missing.map((m) => t(`purchaseLedger.approval.missing.${m}`, { defaultValue: m })).join(', ')}` : '';
      notify(purchaseErrorMessage(t, error) + list, 'error');
      return false;
    } finally {
      setBusy('');
    }
  };

  const approve = () =>
    post(
      'approve',
      {
        totals_checked: totalsChecked,
        accept_new_iban: acceptIban,
        receipt_payment_method: invoice.kind === 'receipt' ? receiptMethod : undefined,
      },
      t('purchaseLedger.approval.approved')
    );

  if (invoice.status === 'cancelled') {
    return (
      <Alert severity="info" data-testid="pl-approval-bar">
        {t('purchaseLedger.approval.cancelledInfo', { reason: invoice.cancel_reason || '' })}
      </Alert>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, p: 2 }} data-testid="pl-approval-bar">
      {invoice.status === 'received' && (
        <>
          {blocking.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="pl-approval-missing">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('purchaseLedger.approval.missingTitle')}</Typography>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {blocking.map((m) => (
                  <li key={m}>
                    <Typography variant="body2">{t(`purchaseLedger.approval.missing.${m}`, { defaultValue: m })}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 1.5 }}>{t('purchaseLedger.approval.ready')}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
            {missing.includes('totals_mismatch') && (
              <FormControlLabel control={<Checkbox checked={totalsChecked} onChange={(e) => setTotalsChecked(e.target.checked)} data-testid="pl-approve-totals-checked" />} label={t('purchaseLedger.approval.totalsChecked')} />
            )}
            {missing.includes('new_iban') && (
              <FormControlLabel control={<Checkbox checked={acceptIban} onChange={(e) => setAcceptIban(e.target.checked)} data-testid="pl-approve-accept-iban" />} label={t('purchaseLedger.approval.acceptIban', { iban: invoice.payee_iban })} />
            )}
            {invoice.kind === 'receipt' && (
              <FormControl size="small" sx={{ maxWidth: 240 }}>
                <InputLabel>{t('purchaseLedger.approval.receiptMethod')}</InputLabel>
                <Select value={receiptMethod} label={t('purchaseLedger.approval.receiptMethod')} onChange={(e) => setReceiptMethod(e.target.value)}>
                  {RECEIPT_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>{t(`purchaseLedger.paymentMethod.${m}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </>
      )}
      {invoice.status === 'disputed' && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t('purchaseLedger.approval.disputedInfo', { reason: invoice.dispute_reason || '' })}
        </Alert>
      )}
      {invoice.status === 'approved' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('purchaseLedger.approval.approvedBy', { name: invoice.approved_by_name || '-', date: invoice.approved_at ? formatDate(invoice.approved_at) : '' })}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {invoice.status === 'received' && canApprove && (
          <Button variant="contained" color="success" disabled={!!busy || blocking.length > 0} onClick={approve} startIcon={busy === 'approve' ? <CircularProgress size={16} /> : <CheckIcon />} data-testid="pl-approve">
            {t('purchaseLedger.approval.approve')}
          </Button>
        )}
        {invoice.status === 'approved' && canApprove && (
          <Button variant="outlined" disabled={!!busy} onClick={() => setUnapproveOpen(true)} data-testid="pl-unapprove">
            {t('purchaseLedger.approval.unapprove')}
          </Button>
        )}
        {(invoice.status === 'received' || invoice.status === 'approved') && canManage && (
          <Button variant="outlined" color="warning" disabled={!!busy} onClick={() => setDisputeOpen(true)} data-testid="pl-dispute">
            {t('purchaseLedger.approval.dispute')}
          </Button>
        )}
        {invoice.status === 'disputed' && canManage && (
          <Button variant="outlined" disabled={!!busy} onClick={() => post('undispute', {}, t('purchaseLedger.approval.undisputed'))} data-testid="pl-undispute">
            {t('purchaseLedger.approval.undispute')}
          </Button>
        )}
        {canManage && (
          <Button variant="text" color="error" disabled={!!busy} onClick={() => setCancelOpen(true)} data-testid="pl-cancel">
            {t('purchaseLedger.approval.cancel')}
          </Button>
        )}
      </Box>
      <ReasonDialog open={disputeOpen} onClose={() => setDisputeOpen(false)} busy={busy === 'dispute'} title={t('purchaseLedger.approval.dispute')} description={t('purchaseLedger.approval.disputeHelp')} confirmLabel={t('purchaseLedger.approval.dispute')} onConfirm={(reason) => post('dispute', { reason }, t('purchaseLedger.approval.disputed')).then((ok) => ok && setDisputeOpen(false))} testId="pl-dispute-dialog" />
      <ReasonDialog open={cancelOpen} onClose={() => setCancelOpen(false)} busy={busy === 'cancel'} title={t('purchaseLedger.approval.cancel')} description={t('purchaseLedger.approval.cancelHelp')} confirmLabel={t('purchaseLedger.approval.cancel')} onConfirm={(reason) => post('cancel', { reason }, t('purchaseLedger.approval.cancelled')).then((ok) => ok && setCancelOpen(false))} testId="pl-cancel-dialog" />
      <ReasonDialog open={unapproveOpen} onClose={() => setUnapproveOpen(false)} busy={busy === 'unapprove'} title={t('purchaseLedger.approval.unapprove')} description={t('purchaseLedger.approval.unapproveHelp')} confirmLabel={t('purchaseLedger.approval.unapprove')} required={false} onConfirm={(reason) => post('unapprove', { reason }, t('purchaseLedger.approval.unapproved')).then((ok) => ok && setUnapproveOpen(false))} testId="pl-unapprove-dialog" />
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Payment card
// ---------------------------------------------------------------------------------------

function BarcodeDialog({ open, onClose, invoiceId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    axios
      .get(invoiceUrl(invoiceId, 'barcode'))
      .then((r) => setData(r.data))
      .catch(() => setData({ digits: '', svg: '', warnings: ['encode_failed'] }));
  }, [open, invoiceId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.digits);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      // clipboard unavailable: the digits stay selectable
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="pl-barcode-dialog">
      <DialogTitle>{t('purchaseLedger.payments.payInBank')}</DialogTitle>
      <DialogContent>
        {!data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {data && (
          <>
            {(data.warnings || []).map((w) => (
              <Alert key={w} severity={data.digits ? 'info' : 'warning'} sx={{ mb: 1 }}>
                {t(`purchaseLedger.barcode.${w}`, { defaultValue: w })}
              </Alert>
            ))}
            {data.digits && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('purchaseLedger.barcode.help')}</Typography>
                {data.svg && (
                  <Box sx={{ bgcolor: '#fff', p: 1, borderRadius: 1, overflowX: 'auto', '& svg': { maxWidth: '100%', height: 'auto' } }} dangerouslySetInnerHTML={{ __html: data.svg }} />
                )}
                <Typography sx={{ ...numFont, fontSize: { xs: 15, sm: 18 }, letterSpacing: '.04em', wordBreak: 'break-all', mt: 1.5, textAlign: 'center' }} data-testid="pl-barcode-digits">
                  {data.digits}
                </Typography>
                <Typography variant="body2" sx={{ ...numFont, textAlign: 'center', mt: 1 }}>
                  {t('purchaseLedger.fields.openAmount')}: <strong>{money(data.amount)}</strong>
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
        {data?.digits && (
          <Button variant={copied ? 'contained' : 'outlined'} color={copied ? 'success' : 'primary'} startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copy} data-testid="pl-barcode-copy">
            {copied ? t('purchaseLedger.barcode.copied') : t('purchaseLedger.barcode.copy')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function MarkPaidDialog({ open, onClose, invoice, onSaved, notify }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState('bank_transfer');
  const [note, setNote] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(invoice.open_amount != null ? String(invoice.open_amount).replace('-', '') : ''); // backend sends a quantised decimal string
      setDate(todayIso());
      setMethod('bank_transfer');
      setNote('');
      setKey(newIdempotencyKey());
    }
  }, [open, invoice.open_amount]);

  const save = async () => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, 'payments'), {
        amount,
        payment_date: date,
        method,
        note,
        idempotency_key: key,
      });
      onSaved(response.data);
      notify(t('purchaseLedger.payments.recorded'));
      onClose();
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth data-testid="pl-mark-paid-dialog">
      <DialogTitle>{t('purchaseLedger.payments.markPaid')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <DecimalField label={t('purchaseLedger.payments.amount')} value={amount} onChange={setAmount} size="small" inputProps={{ 'data-testid': 'pl-mark-paid-amount' }} />
        <TextField type="date" size="small" label={t('purchaseLedger.payments.paymentDate')} value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <FormControl size="small">
          <InputLabel>{t('purchaseLedger.payments.method')}</InputLabel>
          <Select value={method} label={t('purchaseLedger.payments.method')} onChange={(e) => setMethod(e.target.value)}>
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m} value={m}>{t(`purchaseLedger.paymentMethod.${m}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label={t('purchaseLedger.payments.note')} value={note} onChange={(e) => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="contained" onClick={save} disabled={busy || !amount || toNumber(amount) <= 0} startIcon={busy ? <CircularProgress size={16} /> : null} data-testid="pl-mark-paid-save">
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PaymentCard({ invoice, canPay, onSaved, notify }) {
  const { t } = useTranslation();
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [netTarget, setNetTarget] = useState('');

  const reverse = async (reason) => {
    setBusy(true);
    try {
      const response = await axios.post(`${invoiceUrl(invoice.id, 'payments')}${reverseTarget.id}/reverse/`, { reason });
      onSaved(response.data);
      setReverseTarget(null);
      notify(t('purchaseLedger.payments.reversed'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const netCreditNote = async () => {
    // The credit note is applied to the ORIGINAL invoice: POST on the original with {credit_note}.
    setBusy(true);
    try {
      const originalId = invoice.kind === 'credit_note' ? invoice.original_credit_note_of : invoice.id;
      const creditNoteId = invoice.kind === 'credit_note' ? invoice.id : netTarget;
      const response = await axios.post(invoiceUrl(originalId, 'net-credit-note'), { credit_note: creditNoteId });
      if (originalId === invoice.id) onSaved(response.data);
      else {
        const refreshed = await axios.get(invoiceUrl(invoice.id));
        onSaved(refreshed.data);
      }
      notify(t('purchaseLedger.payments.netted'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const payments = invoice.payments || [];
  const open = toNumber(invoice.open_amount);
  const approved = invoice.status === 'approved';
  const payable = approved && invoice.kind === 'invoice' && invoice.payment_state !== 'paid';
  const creditNotes = (invoice.credit_notes || []).filter((cn) => cn.status === 'approved' && cn.payment_state !== 'paid');
  const canNetAsCreditNote = invoice.kind === 'credit_note' && approved && invoice.payment_state !== 'paid' && invoice.original_credit_note_of;

  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }} data-testid="pl-payment-card">
      <CardHeader title={t('purchaseLedger.payments.title')} icon={<PaymentsIcon fontSize="small" />} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.fields.totalGross')}</Typography>
            <Typography sx={{ ...numFont, fontWeight: 700 }}>{money(invoice.total_gross)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.payments.paid')}</Typography>
            <Typography sx={{ ...numFont, fontWeight: 700 }}>{money(invoice.paid_amount)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.fields.openAmount')}</Typography>
            <Typography sx={{ ...numFont, fontWeight: 700, color: open > 0 ? 'inherit' : 'success.main' }} data-testid="pl-open-amount">{money(invoice.open_amount)}</Typography>
          </Box>
          <Box sx={{ alignSelf: 'center' }}>
            <StatusChip status={invoice.status} paymentState={invoice.payment_state} inBatch={!!invoice.open_batch} size="medium" />
          </Box>
        </Box>
        {invoice.open_batch && (
          <Alert severity="info" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" component={RouterLink} to={`/ostolaskut/maksuerat/${invoice.open_batch.id}`}>{invoice.open_batch.number}</Button>}>
            {t('purchaseLedger.payments.inBatch')}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: payments.length ? 2 : 0 }}>
          {invoice.kind === 'invoice' && invoice.status !== 'cancelled' && (
            <Button variant="outlined" startIcon={<QrCode2Icon />} onClick={() => setBarcodeOpen(true)} data-testid="pl-barcode">
              {t('purchaseLedger.payments.payInBank')}
            </Button>
          )}
          {canPay && approved && invoice.payment_state !== 'paid' && (
            <Button variant="contained" startIcon={<PaymentsIcon />} onClick={() => setMarkPaidOpen(true)} disabled={!!invoice.open_batch} data-testid="pl-mark-paid">
              {t('purchaseLedger.payments.markPaid')}
            </Button>
          )}
          {canPay && payable && !invoice.open_batch && (
            <Button variant="outlined" component={RouterLink} to={`/ostolaskut/maksuun?ids=${invoice.id}`}>
              {t('purchaseLedger.payments.toBatch')}
            </Button>
          )}
          {canPay && canNetAsCreditNote && (
            <Button variant="outlined" color="warning" disabled={busy} onClick={netCreditNote} data-testid="pl-net-credit-note">
              {t('purchaseLedger.payments.netToInvoice')}
            </Button>
          )}
        </Box>
        {canPay && payable && creditNotes.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('purchaseLedger.payments.chooseCreditNote')}</InputLabel>
              <Select value={netTarget} label={t('purchaseLedger.payments.chooseCreditNote')} onChange={(e) => setNetTarget(e.target.value)}>
                {creditNotes.map((cn) => (
                  <MenuItem key={cn.id} value={cn.id}>{cn.number} · {money(cn.total_gross)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" color="warning" disabled={!netTarget || busy} onClick={netCreditNote}>
              {t('purchaseLedger.payments.netCreditNote')}
            </Button>
          </Box>
        )}
        {payments.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('purchaseLedger.payments.paymentDate')}</TableCell>
                  <TableCell>{t('purchaseLedger.payments.method')}</TableCell>
                  <TableCell>{t('purchaseLedger.payments.source')}</TableCell>
                  <TableCell align="right">{t('purchaseLedger.payments.amount')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id} sx={{ opacity: p.reversed_at || p.reverses ? 0.6 : 1 }} data-testid={`pl-payment-${p.id}`}>
                    <TableCell sx={numFont}>{formatDateOnly(p.payment_date)}</TableCell>
                    <TableCell>{t(`purchaseLedger.paymentMethod.${p.method}`, { defaultValue: p.method })}</TableCell>
                    <TableCell>
                      {t(`purchaseLedger.paymentSource.${p.source}`, { defaultValue: p.source })}
                      {p.batch_number ? ` · ${p.batch_number}` : ''}
                      {p.note ? ` · ${p.note}` : ''}
                      {p.reverses ? ` (${t('purchaseLedger.payments.reversal')})` : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{money(p.amount)}</TableCell>
                    <TableCell align="right">
                      {canPay && !p.reverses && !p.reversed_at && p.source !== 'batch' && (
                        <Button size="small" color="inherit" disabled={busy} onClick={() => setReverseTarget(p)} data-testid={`pl-reverse-payment-${p.id}`}>
                          {t('purchaseLedger.payments.reverse')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
      <BarcodeDialog open={barcodeOpen} onClose={() => setBarcodeOpen(false)} invoiceId={invoice.id} />
      <MarkPaidDialog open={markPaidOpen} onClose={() => setMarkPaidOpen(false)} invoice={invoice} onSaved={onSaved} notify={notify} />
      <ReasonDialog open={!!reverseTarget} onClose={() => setReverseTarget(null)} busy={busy} title={t('purchaseLedger.payments.reverse')} description={t('purchaseLedger.payments.reverseHelp')} confirmLabel={t('purchaseLedger.payments.reverse')} onConfirm={reverse} testId="pl-reverse-dialog" />
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Accounting card
// ---------------------------------------------------------------------------------------

function AccountingCard({ invoice, canManage, accountingProvider, onSaved, notify }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const transfers = invoice.transfers || [];
  const latest = transfers[0];

  const push = async () => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, 'push-to-accounting'), {});
      onSaved(response.data);
      notify(t('purchaseLedger.accounting.queued'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const stateColor = { success: 'success', failed: 'error', pending: 'info', in_progress: 'info', skipped: 'default' };

  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }} data-testid="pl-accounting-card">
      <CardHeader title={t('purchaseLedger.accounting.title')} icon={<AccountBalanceIcon fontSize="small" />} />
      <Box sx={{ p: 2 }}>
        {!accountingProvider && (
          <Typography variant="body2" color="text.secondary">{t('purchaseLedger.accounting.noIntegration')}</Typography>
        )}
        {accountingProvider && !latest && (
          <Typography variant="body2" color="text.secondary">{t('purchaseLedger.accounting.notSent', { provider: accountingProvider })}</Typography>
        )}
        {latest && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color={stateColor[latest.status] || 'default'} label={t(`purchaseLedger.accounting.state.${latest.status}`, { defaultValue: latest.status })} data-testid="pl-transfer-state" />
            <Typography variant="body2" color="text.secondary">{latest.provider}</Typography>
            {latest.external_id && <Typography variant="caption" sx={numFont}>#{latest.external_id}</Typography>}
            {latest.transferred_at && <Typography variant="caption" color="text.secondary" sx={numFont}>{formatDate(latest.transferred_at)}</Typography>}
          </Box>
        )}
        {latest?.status === 'failed' && latest.error_message && (
          <Alert severity="error" sx={{ mt: 1 }}>{latest.error_message}</Alert>
        )}
        {canManage && accountingProvider && invoice.status === 'approved' && (
          <Button size="small" variant="outlined" sx={{ mt: 1.5 }} disabled={busy || latest?.status === 'in_progress'} onClick={push} data-testid="pl-push-accounting">
            {latest ? t('purchaseLedger.accounting.resend') : t('purchaseLedger.accounting.send')}
          </Button>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------------------
// Flags banner, lines, events
// ---------------------------------------------------------------------------------------

function FlagsBanner({ invoice, canManage, onSaved, notify }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const flags = invoice.flags || [];
  if (flags.length === 0) return null;

  const post = async (action, payload = {}) => {
    setBusy(true);
    try {
      const response = await axios.post(invoiceUrl(invoice.id, action), payload);
      onSaved(response.data);
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="pl-flags-banner">
      {flags.map((flag) => (
        <Alert
          key={flag}
          severity={FLAG_SEVERITY[flag] || 'info'}
          data-testid={`pl-flag-banner-${flag}`}
          action={
            flag === 'duplicate_suspect' && canManage && invoice.status === 'received' ? (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button color="inherit" size="small" disabled={busy} onClick={() => post('not-duplicate')} data-testid="pl-not-duplicate">
                  {t('purchaseLedger.flags.notDuplicate')}
                </Button>
                {invoice.duplicate_of && (
                  <Button color="inherit" size="small" disabled={busy} onClick={() => post('cancel', { reason: 'duplicate', duplicate_of: invoice.duplicate_of })} data-testid="pl-mark-duplicate">
                    {t('purchaseLedger.flags.markDuplicate')}
                  </Button>
                )}
              </Box>
            ) : null
          }
        >
          {t(`purchaseLedger.flags.${flag}`, { defaultValue: flag })}
          {flag === 'duplicate_suspect' && invoice.duplicate_of_detail && (
            <>
              {' '}
              <Link component={RouterLink} to={`/ostolaskut/${invoice.duplicate_of_detail.id}`}>
                {invoice.duplicate_of_detail.number} · {invoice.duplicate_of_detail.invoice_number} · {money(invoice.duplicate_of_detail.total_gross)}
              </Link>
            </>
          )}
        </Alert>
      ))}
    </Box>
  );
}

function LinesAccordion({ lines }) {
  const { t } = useTranslation();
  if (!lines || lines.length === 0) return null;
  return (
    <Accordion elevation={0} sx={{ borderRadius: 2, '&:before': { display: 'none' } }} data-testid="pl-lines">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>{t('purchaseLedger.lines.title', { count: lines.length })}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ overflowX: 'auto', p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('purchaseLedger.lines.code')}</TableCell>
              <TableCell>{t('purchaseLedger.lines.name')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.lines.quantity')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.lines.unitPrice')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.lines.vatRate')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.lines.total')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ ...codeFont, fontSize: 13 }}>{l.article_code}</TableCell>
                <TableCell>{l.article_name}</TableCell>
                <TableCell align="right" sx={numFont}>{l.quantity != null ? `${formatQuantity(l.quantity)} ${l.unit || ''}` : ''}</TableCell>
                <TableCell align="right" sx={numFont}>{l.unit_price_net != null ? formatPrice(l.unit_price_net) : ''}</TableCell>
                <TableCell align="right" sx={numFont}>{l.vat_rate != null ? `${formatQuantity(l.vat_rate)} %` : ''}</TableCell>
                <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{l.line_gross != null ? money(l.line_gross) : l.line_net != null ? money(l.line_net) : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

function EventsAccordion({ invoiceId, refreshKey }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios
      .get(invoiceUrl(invoiceId, 'events'))
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, [open, invoiceId, refreshKey]);

  const describe = (e) => {
    const base = t(`purchaseLedger.events.${e.event}`, { defaultValue: e.event });
    const p = e.payload || {};
    const extras = [];
    if (p.field) extras.push(`${p.field}: ${p.old ?? '-'} -> ${p.new ?? '-'}`);
    if (p.reason) extras.push(p.reason);
    if (p.amount) extras.push(money(p.amount));
    if (p.batch) extras.push(p.batch);
    if (p.credit_note) extras.push(p.credit_note);
    if (p.invoice_number) extras.push(p.invoice_number);
    if (p.provider) extras.push(p.provider);
    if (p.error) extras.push(p.error);
    return extras.length ? `${base} (${extras.join(', ')})` : base;
  };

  return (
    <Accordion elevation={0} expanded={open} onChange={() => setOpen((v) => !v)} sx={{ borderRadius: 2, '&:before': { display: 'none' } }} data-testid="pl-events">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon fontSize="small" /> {t('purchaseLedger.events.title')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!events && <CircularProgress size={20} />}
        {events && events.length === 0 && <Typography variant="body2" color="text.secondary">{t('purchaseLedger.events.none')}</Typography>}
        {events && events.map((e) => (
          <Box key={e.id} sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ ...numFont, whiteSpace: 'nowrap', minWidth: 120 }}>{formatDate(e.created_at)}</Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>{describe(e)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{e.actor_name || t('purchaseLedger.events.system')}</Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------------

function PurchaseInvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const canManage = can(currentOrganization, 'purchase_invoices.manage');
  const canApprove = can(currentOrganization, 'purchase_invoices.approve');
  const canPay = can(currentOrganization, 'purchase_invoices.pay');
  const accountingProvider = currentOrganization?.accounting_integration_provider || '';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [reextracting, setReextracting] = useState(false);

  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(invoiceUrl(id));
      setInvoice(response.data);
    } catch (error) {
      if (error.response?.status === 404) setNotFound(true);
      else notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    load();
    axios
      .get(`${PL_API}/cost-categories/`)
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : r.data?.results || []))
      .catch(() => setCategories([]));
  }, [load]);

  const onSaved = useCallback((data) => {
    setInvoice(data);
    setRefreshKey((k) => k + 1);
  }, []);

  const reextract = async () => {
    setReextracting(true);
    try {
      const response = await axios.post(invoiceUrl(id, 'reextract'), {});
      onSaved(response.data);
      notify(t('purchaseLedger.detail.reextracted'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setReextracting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (notFound || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('purchaseLedger.detail.notFound')}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate('/ostolaskut')}>{t('purchaseLedger.backToList')}</Button>
      </Box>
    );
  }

  const editable = canManage && invoice.status === 'received';
  const vendorName = invoice.vendor_detail?.name || invoice.vendor_name_on_document;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="purchase-invoice-detail">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ostolaskut')} sx={{ mb: 1 }}>
        {t('purchaseLedger.backToList')}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }} data-testid="pl-detail-vendor">
            {vendorName || t('purchaseLedger.vendor.unknown')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={numFont} data-testid="pl-detail-number">
            {invoice.number}
          </Typography>
          <StatusChip status={invoice.status} paymentState={invoice.payment_state} inBatch={!!invoice.open_batch} size="medium" />
          <KindChip kind={invoice.kind} />
          <FlagIcons flags={invoice.flags} />
        </Box>
        {editable && invoice.extraction_source !== 'finvoice' && (invoice.attachments || []).some((a) => a.is_viewable_inline) && (
          <Button variant="outlined" size="small" startIcon={reextracting ? <CircularProgress size={14} /> : <RefreshIcon />} disabled={reextracting} onClick={reextract} data-testid="pl-reextract">
            {t('purchaseLedger.detail.reextract')}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 4fr)' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <DocumentPane invoice={invoice} canManage={canManage} onAttachmentAdded={load} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <FlagsBanner invoice={invoice} canManage={canManage} onSaved={onSaved} notify={notify} />
          <ApprovalBar invoice={invoice} canApprove={canApprove} canManage={canManage} onSaved={onSaved} notify={notify} />
          <VendorCard invoice={invoice} editable={editable} onSaved={onSaved} notify={notify} />
          <FieldsCard invoice={invoice} categories={categories} editable={editable} onSaved={onSaved} notify={notify} />
          <LinksCard invoice={invoice} editable={canManage && invoice.status !== 'cancelled'} onSaved={onSaved} notify={notify} />
          <PaymentCard invoice={invoice} canPay={canPay} onSaved={onSaved} notify={notify} />
          <AccountingCard invoice={invoice} canManage={canManage} accountingProvider={accountingProvider} onSaved={onSaved} notify={notify} />
          <LinesAccordion lines={invoice.lines} />
          <EventsAccordion invoiceId={invoice.id} refreshKey={refreshKey} />
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PurchaseInvoiceDetail;
